import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import Button from '../../components/ui/Button';
import { 
  Calendar, 
  Clock, 
  Users, 
  BookOpen, 
  ArrowLeft, 
  Edit3, 
  Mail, 
  Phone, 
  User,
  TrendingUp,
  Activity,
  AlertCircle,
  CheckCircle2,
  XCircle
} from 'lucide-react';

const ClassViewPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { classId } = useParams();
  
  // State
  const [classData, setClassData] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Load class data and members
  useEffect(() => {
    const loadClassData = async () => {
      if (!user || !classId) return;

      try {
        setLoading(true);
        setError(null);

        // Verify admin status
        const adminStatus = await api.admin.verifyAdmin(user.$id);
        if (!adminStatus) {
          navigate('/classes');
          return;
        }
        setIsAdmin(true);

        // Load class details
        const classDetails = await api.classes.getDetails(classId);
        if (!classDetails) {
          throw new Error('Class not found');
        }
        setClassData(classDetails);

        // Load member details if there are any members
        if (classDetails.members && classDetails.members.length > 0) {
          const memberDetails = await api.admin.getClassMembers(classDetails.members);
          setMembers(memberDetails);
        } else {
          setMembers([]);
        }

      } catch (err) {
        console.error('Error loading class data:', err);
        setError(err.message || 'Failed to load class details');
      } finally {
        setLoading(false);
      }
    };

    loadClassData();
  }, [user, classId, navigate]);

  // Calculate stats
  const getStats = () => {
    if (!classData) return {};

    const totalSpots = classData.totalSpots || 0;
    const currentMembers = members.length;
    const spotsLeft = totalSpots - currentMembers;
    const fillRate = totalSpots > 0 ? Math.round((currentMembers / totalSpots) * 100) : 0;
    const daysActive = Math.floor((new Date() - new Date(classData.$createdAt)) / (1000 * 60 * 60 * 24));

    return {
      totalSpots,
      currentMembers,
      spotsLeft,
      fillRate,
      daysActive
    };
  };

  const stats = getStats();

  // Get status color and icon
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'active':
        return {
          color: 'text-green-400',
          bgColor: 'bg-green-500 bg-opacity-20',
          icon: <CheckCircle2 size={16} />,
          text: 'Active'
        };
      case 'cancelled':
        return {
          color: 'text-red-400',
          bgColor: 'bg-red-500 bg-opacity-20',
          icon: <XCircle size={16} />,
          text: 'Cancelled'
        };
      case 'completed':
        return {
          color: 'text-gray-400',
          bgColor: 'bg-gray-500 bg-opacity-20',
          icon: <CheckCircle2 size={16} />,
          text: 'Completed'
        };
      case 'full':
        return {
          color: 'text-blue-400',
          bgColor: 'bg-blue-500 bg-opacity-20',
          icon: <Users size={16} />,
          text: 'Full'
        };
      default:
        return {
          color: 'text-gray-400',
          bgColor: 'bg-gray-500 bg-opacity-20',
          icon: <AlertCircle size={16} />,
          text: status || 'Unknown'
        };
    }
  };

  const statusDisplay = classData ? getStatusDisplay(classData.status) : null;

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Invalid date';
    }
  };

  // Handle navigation
  const handleBack = () => {
    navigate('/admin');
  };

  const handleEdit = () => {
    navigate(`/admin/edit-class/${classId}`);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-purple-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-400">Loading class details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white">
        <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-10 shadow-md">
          <div className="container mx-auto p-4 flex justify-between items-center">
            <h1 className="text-xl font-bold">
              <span className="bg-gradient-to-r from-purple-400 to-indigo-500 text-transparent bg-clip-text">
                Admin Dashboard
              </span>
            </h1>
          </div>
          <div className="h-1 bg-gradient-to-r from-purple-600 via-indigo-500 to-purple-600"></div>
        </header>

        <main className="container mx-auto py-8 px-4">
          <div className="max-w-md mx-auto text-center">
            <div className="mb-6">
              <XCircle size={48} className="text-red-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Error Loading Class</h2>
              <p className="text-gray-400 mb-6">{error}</p>
              <Button variant="primary" onClick={handleBack}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!classData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-10 shadow-md">
        <div className="container mx-auto p-4 flex justify-between items-center">
          <h1 className="text-xl font-bold">
            <span className="bg-gradient-to-r from-purple-400 to-indigo-500 text-transparent bg-clip-text">
              Admin Dashboard
            </span>
          </h1>
          
          <div className="flex items-center space-x-4">
            <span className="text-gray-300 text-sm hidden sm:inline">
              Admin: {user?.name}
            </span>
          </div>
        </div>
        <div className="h-1 bg-gradient-to-r from-purple-600 via-indigo-500 to-purple-600"></div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Back button and actions */}
          <div className="flex justify-between items-center mb-6">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="text-gray-400 hover:text-white flex items-center"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Dashboard
            </Button>

            {classData.status === 'active' && (
              <Button
                variant="primary"
                onClick={handleEdit}
                className="bg-purple-600 hover:bg-purple-700 flex items-center"
              >
                <Edit3 size={16} className="mr-2" />
                Edit Class
              </Button>
            )}
          </div>

          {/* Page title */}
          <div className="text-center mb-8">
            <div className="inline-block text-6xl font-light text-purple-400 opacity-20 mb-2">
              课程详情
            </div>
            <h2 className="text-2xl font-bold">Class Details</h2>
            <div className="w-16 h-1 bg-purple-500 mx-auto mt-2"></div>
          </div>

          {/* Class Overview */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Basic Information */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-4 border-b border-gray-700">
                <h3 className="font-semibold text-lg flex items-center">
                  <Calendar size={20} className="mr-2" />
                  Class Information
                </h3>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <BookOpen size={16} className="text-purple-400 mr-3" />
                    <span className="text-gray-400">Type</span>
                  </div>
                  <span className="font-medium text-purple-300">
                    {classData.type.charAt(0).toUpperCase() + classData.type.slice(1)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Calendar size={16} className="text-purple-400 mr-3" />
                    <span className="text-gray-400">Day</span>
                  </div>
                  <span className="font-medium">{classData.day}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Clock size={16} className="text-purple-400 mr-3" />
                    <span className="text-gray-400">Time</span>
                  </div>
                  <span className="font-medium">{classData.time}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Activity size={16} className="text-purple-400 mr-3" />
                    <span className="text-gray-400">Status</span>
                  </div>
                  <span className={`flex items-center px-3 py-1 rounded-full text-sm ${statusDisplay.bgColor} ${statusDisplay.color}`}>
                    {statusDisplay.icon}
                    <span className="ml-2">{statusDisplay.text}</span>
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Calendar size={16} className="text-purple-400 mr-3" />
                    <span className="text-gray-400">Created</span>
                  </div>
                  <span className="font-medium text-sm">{formatDate(classData.$createdAt)}</span>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-green-800 to-green-700 px-6 py-4 border-b border-gray-700">
                <h3 className="font-semibold text-lg flex items-center">
                  <TrendingUp size={20} className="mr-2" />
                  Statistics
                </h3>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Enrollment</span>
                  <span className="font-medium text-green-400">
                    {stats.currentMembers} / {stats.totalSpots}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Fill Rate</span>
                  <span className="font-medium text-green-400">{stats.fillRate}%</span>
                </div>

                <div className="w-full bg-gray-700 rounded-full h-3">
                  <div
                    className="bg-green-500 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${stats.fillRate}%` }}
                  ></div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Spots Left</span>
                  <span className="font-medium">{stats.spotsLeft}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Days Active</span>
                  <span className="font-medium">{stats.daysActive}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Enrolled Students */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-800 to-blue-700 px-6 py-4 border-b border-gray-700">
              <h3 className="font-semibold text-lg flex items-center justify-between">
                <div className="flex items-center">
                  <Users size={20} className="mr-2" />
                  Enrolled Students ({members.length})
                </div>
                {members.length > 0 && (
                  <Button
                    variant="ghost"
                    size="small"
                    className="text-blue-300 hover:text-white"
                  >
                    <Mail size={16} className="mr-2" />
                    Contact All
                  </Button>
                )}
              </h3>
            </div>
            
            <div className="p-6">
              {members.length === 0 ? (
                <div className="text-center py-8">
                  <Users size={48} className="text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400 mb-2">No students enrolled yet</p>
                  <p className="text-gray-500 text-sm">
                    Students will appear here when they join the class
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {members.map((member, index) => (
                    <div
                      key={member.userId || index}
                      className="bg-gray-700 bg-opacity-50 rounded-lg p-4 border border-gray-600"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                          <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                            <User size={20} className="text-white" />
                          </div>
                          
                          <div className="flex-1">
                            <h4 className="font-medium text-white mb-1">
                              {member.name || 'Unknown Name'}
                            </h4>
                            
                            <div className="space-y-1 text-sm">
                              <div className="flex items-center text-gray-400">
                                <Mail size={14} className="mr-2" />
                                {member.email || 'No email'}
                              </div>
                              
                              {member.phone && (
                                <div className="flex items-center text-gray-400">
                                  <Phone size={14} className="mr-2" />
                                  {member.phone}
                                </div>
                              )}
                              
                              <div className="flex items-center text-gray-500 text-xs">
                                <Calendar size={12} className="mr-2" />
                                Joined: {formatDate(member.joinedAt)}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex space-x-2">
                          <Button
                            variant="ghost"
                            size="small"
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <Mail size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="small"
                            className="text-green-400 hover:text-green-300"
                          >
                            <Phone size={14} />
                          </Button>
                        </div>
                      </div>
                      
                      {member.error && (
                        <div className="mt-2 text-xs text-red-400">
                          Note: {member.error}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            <Button
              variant="outline"
              className="border-purple-500 text-purple-400 hover:bg-purple-500 hover:bg-opacity-10"
            >
              Duplicate Class
            </Button>
            
            <Button
              variant="outline"
              className="border-blue-500 text-blue-400 hover:bg-blue-500 hover:bg-opacity-10"
            >
              Export Members
            </Button>
            
            {classData.status === 'active' && (
              <Button
                variant="outline"
                className="border-red-500 text-red-400 hover:bg-red-500 hover:bg-opacity-10"
              >
                Cancel Class
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClassViewPage;